import { alertController } from "@ionic/vue";
import { deleteDraft, DRAFT_KIND_TITLES, loadDraft, summarizeDraft, type Draft, type DraftKind } from "@/helper/drafts";

export type DraftChoice = "continue" | "new" | "abort";

export interface DraftPromptResult {
  choice: DraftChoice;
  /** The draft to apply when `choice` is `continue`. */
  draft: Draft | null;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Asks what to do with a saved draft before its dialog opens. Without a draft it answers `new` right away.
 * Choosing to start new deletes the draft.
 */
export async function askToContinueDraft(kind: DraftKind, scope: string): Promise<DraftPromptResult> {
  const draft = loadDraft(kind, scope);
  if (draft === null) return { choice: "new", draft: null };

  const fields = summarizeDraft(draft);
  const saved = new Date(draft.updatedAt).toLocaleString();
  const alert = await alertController.create({
    header: `${DRAFT_KIND_TITLES[kind]}: unsaved draft`,
    subHeader: `${draft.label} · saved ${saved}`,
    message: fields.length ? `Saved fields:<br>• ${fields.map(escapeHtml).join("<br>• ")}` : "The draft has no filled in fields.",
    backdropDismiss: false,
    buttons: [
      { text: "Cancel", role: "cancel" },
      { text: "Delete draft, start new", role: "destructive" },
      { text: "Continue draft", role: "continue" },
    ],
  });
  await alert.present();
  const { role } = await alert.onDidDismiss();
  if (role === "continue") return { choice: "continue", draft };
  if (role === "destructive") {
    deleteDraft(kind, scope);
    return { choice: "new", draft: null };
  }
  return { choice: "abort", draft: null };
}
